import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { ApiError, type ChatMessage, type SendMessageRequest } from "@ferrivia/api-contract";
import { useApi, useTracker } from "@ferrivia/auth-web";
import type { ThreadDescriptor } from "@ferrivia/chat-ui";
import { useTranslation } from "@ferrivia/i18n";
import { Lock } from "lucide-react";
import { ThreadView } from "../../components/ThreadView";
import { useRefreshChatSummary } from "../../hooks/useChatSummary";

const HUMAN_DESCRIPTOR_ID = "human";

function isOwnMessage(message: ChatMessage): boolean {
  return message.authorType === "client";
}

export function ChatScreen() {
  const api = useApi();
  const tracker = useTracker();
  const { t } = useTranslation("client");
  const { pathname } = useLocation();
  const isActive = pathname === "/chat";
  const [activeDescriptorId, setActiveDescriptorId] = useState(HUMAN_DESCRIPTOR_ID);

  const descriptors = useMemo<ThreadDescriptor[]>(
    () => [
      {
        id: HUMAN_DESCRIPTOR_ID,
        channel: "human",
        headerName: t("chat.headerName", "Ferrivian terveystiimi"),
        headerStatus: t("chat.headerStatus", "Vastaamme yleensä vuorokauden sisällä"),
        avatarLabel: t("chat.avatarLabel", "FC"),
        composerPlaceholder: t("chat.composerPlaceholder", "Viesti terveystiimillesi…"),
      },
    ],
    [t],
  );

  // Fallback for a staff message whose sender has no eID identity on file
  // yet (e.g. local dev's fake auth provider never sets given/family name).
  const fallbackStaffName = t("chat.fallbackStaffName", "Terveystiimi");
  const senderName = useCallback(
    (message: ChatMessage) => (message.authorType === "staff" ? (message.authorName ?? fallbackStaffName) : undefined),
    [fallbackStaffName],
  );

  const transport = useMemo(
    () => ({
      listMessages: (after?: number) => api.chat.messages(after),
      send: (input: SendMessageRequest) => {
        tracker.track("chat.send_attempted", {});
        return api.chat.send(input).catch((err: unknown) => {
          tracker.track("chat.send_failed", { reason: err instanceof ApiError ? err.code : "network" });
          throw err;
        });
      },
    }),
    [api, tracker],
  );

  const wasActiveRef = useRef(false);
  useEffect(() => {
    if (isActive && !wasActiveRef.current) {
      tracker.track("chat.screen_viewed", {});
    }
    wasActiveRef.current = isActive;
  }, [isActive, tracker]);

  // Opening the thread is what clears the unread count, so the surfaces
  // showing that count are re-read as soon as the server has taken the mark
  // rather than on their own cadence — the tab badge otherwise sat lit for up
  // to 45s while the client was already reading the messages it counted.
  const refreshChatSummary = useRefreshChatSummary();
  const handleMessagesSeen = useCallback(
    (upToSeq: number) => {
      api.chat
        .markRead(upToSeq)
        .then(refreshChatSummary)
        .catch(() => {
          // Best-effort: an unread badge that's briefly stale isn't worth surfacing an error for.
        });
    },
    [api, refreshChatSummary],
  );

  return (
    <ThreadView
      descriptors={descriptors}
      activeDescriptorId={activeDescriptorId}
      onActiveDescriptorChange={setActiveDescriptorId}
      transport={transport}
      isOwnMessage={isOwnMessage}
      senderName={senderName}
      topNote={
        <>
          <Lock size={12} aria-hidden="true" />
          {t("chat.topNote", "Turvallinen, yksityinen kanava terveystiimiisi")}
        </>
      }
      isActive={isActive}
      onMessagesSeen={handleMessagesSeen}
    />
  );
}
